import { Cartesian3 } from "cesium";
import { MeasurementOperation, } from "./measurement-interface";
import { CrossSectionMeasurement } from "./cross-section-measurement";
import { LinearMeasurement } from "./linear-measurement";
import { DistanceMeasurement } from "./distance-measurement";
import { createMetricDistanceUnitPort, } from "../settings/distance-unit";
export class MeasurementManager {
    _measurementMap = new Map();
    _site;
    _viewer;
    _distanceUnitPort;
    constructor(site, viewer, distanceUnitPort = createMetricDistanceUnitPort()) {
        this._site = site;
        this._viewer = viewer;
        this._distanceUnitPort = distanceUnitPort;
    }
    async load(id, type, data) {
        let measurement = undefined;
        const start = Cartesian3.fromArray(data.start);
        const end = Cartesian3.fromArray(data.end);
        switch (type) {
            case MeasurementOperation.LINEAR:
                measurement = new LinearMeasurement(start, end, this._site, this._viewer);
                await measurement.update(start, end);
                break;
            case MeasurementOperation.CROSS_SECTION:
                measurement = new CrossSectionMeasurement(start, end, this._site, this._viewer);
                await measurement.update(start, end);
                break;
            case MeasurementOperation.DISTANCE:
                measurement = new DistanceMeasurement(start, end, this._site, this._viewer, this._distanceUnitPort);
                await measurement.update(start, end);
                break;
            default:
                console.warn(`Incompatible measurement type ${type}`);
                break;
        }
        if (measurement) {
            // If already present then check the type
            if (!this._measurementMap.has(id)) {
                const map = new Map();
                map.set(type, measurement);
                this._measurementMap.set(id, map);
            }
            else if (!this._measurementMap.get(id).has(type)) {
                this._measurementMap.get(id).set(type, measurement);
            }
            else {
                console.error(`Measurement entry with id ${id} and type ${type} is already present`);
            }
        }
        return;
    }
    async show(id, type) {
        await this._measurementMap.get(id)?.get(type)?.recalculate();
        this._measurementMap.get(id)?.get(type)?.updateVisibility(true);
        return;
    }
    hide(id, type) {
        this._measurementMap.get(id)?.get(type)?.updateVisibility(false);
    }
    edit(id, type, data) {
        const start = Cartesian3.fromArray(data.start);
        const end = Cartesian3.fromArray(data.end);
        this._measurementMap.get(id)?.get(type)?.update(start, end);
    }
    delete(id) {
        let pmi = this._measurementMap.get(id);
        if (pmi) {
            pmi.forEach((p) => {
                p.dispose();
                p = undefined;
            });
            this._measurementMap.delete(id);
            pmi = undefined;
        }
    }
    clearAll() {
        this._measurementMap.forEach((m, id) => this.delete(id));
        this._measurementMap.clear();
    }
    // TODO: These type-specific accessors use instanceof because the shared
    // IMeasurementOperation interface cannot express per-class return types.
    // Refactor when IMeasurementOperation is redesigned (see measurement-interface.ts).
    getCrossSectionData(id) {
        const measurement = this._measurementMap
            .get(id)
            ?.get(MeasurementOperation.CROSS_SECTION);
        if (measurement instanceof CrossSectionMeasurement) {
            return measurement.getData();
        }
        return undefined;
    }
    getLinearData(id) {
        const measurement = this._measurementMap
            .get(id)
            ?.get(MeasurementOperation.LINEAR);
        if (measurement instanceof LinearMeasurement) {
            return measurement.getData();
        }
        return undefined;
    }
    getDistanceData(id) {
        const measurement = this._measurementMap
            .get(id)
            ?.get(MeasurementOperation.DISTANCE);
        if (measurement instanceof DistanceMeasurement) {
            return measurement.getData();
        }
        return undefined;
    }
    zoomTo(id, type) {
        this._measurementMap.get(id)?.get(type)?.zoomTo();
    }
    async update(id, type) {
        await this._measurementMap.get(id)?.get(type)?.recalculate();
    }
    async updateCrossSectionByAssetType(assetType) {
        for (const [, typeMap] of this._measurementMap) {
            const crossSection = typeMap.get(MeasurementOperation.CROSS_SECTION);
            if (crossSection instanceof CrossSectionMeasurement) {
                await crossSection.updateByAssetType(assetType);
            }
        }
    }
    async updateUnit(unitPort) {
        this._distanceUnitPort = unitPort;
        const promises = [];
        for (const [, typeMap] of this._measurementMap) {
            for (const [, measurement] of typeMap) {
                if (measurement instanceof DistanceMeasurement) {
                    promises.push(measurement.updateUnit(unitPort));
                    continue;
                }
                promises.push(measurement.recalculate());
            }
        }
        await Promise.all(promises);
    }
}
//# sourceMappingURL=earthbrain-measurement.js.map